import { Helmet } from "react-helmet-async";
import { getAppName } from "@/lib/config.ts";
import SettingsTitle from "@/components/settings/settings-title.tsx";
import React from "react";
import useUserRole from "@/hooks/use-user-role.tsx";
import { useTranslation } from "react-i18next";
import EnableAiSearch from "@/ee/ai/components/enable-ai-search.tsx";
import EnableGenerativeAi from "@/ee/ai/components/enable-generative-ai.tsx";
import EnableAiChat from "@/ee/ai-chat/components/enable-ai-chat.tsx";
import McpSettings from "@/ee/ai/components/mcp-settings.tsx";
import AskAiSpaces from "@/ee/ai/components/ask-ai-spaces.tsx";
import AskAiKnowledgeGaps from "@/ee/ai/components/ask-ai-knowledge-gaps.tsx";
import { Alert, Stack, Tabs } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useHasFeature } from "@/ee/hooks/use-feature";
import { Feature } from "@/ee/features";
import { useUpgradeLabel } from "@/ee/hooks/use-upgrade-label";
import { isCloud } from "@/lib/config.ts";
import { useLocation, useNavigate } from "react-router-dom";

export default function AiSettings() {
  const { t } = useTranslation();
  const { isAdmin } = useUserRole();
  const hasAccess = useHasFeature(Feature.AI);
  const upgradeLabel = useUpgradeLabel();
  const location = useLocation();
  const navigate = useNavigate();

  const activeTab = location.pathname.endsWith("/mcp")
    ? "mcp"
    : location.pathname.endsWith("/gaps")
      ? "gaps"
      : location.pathname.endsWith("/ask")
        ? "ask"
        : "ai";

  if (!isAdmin) {
    return null;
  }

  const handleTabChange = (value: string | null) => {
    if (value === "mcp") {
      navigate("/settings/ai/mcp");
    } else if (value === "ask") {
      navigate("/settings/ai/ask");
    } else if (value === "gaps") {
      navigate("/settings/ai/gaps");
    } else {
      navigate("/settings/ai");
    }
  };

  return (
    <>
      <Helmet>
        <title>AI settings - {getAppName()}</title>
      </Helmet>
      <SettingsTitle title={t("AI settings")} />

      <Tabs color="dark" value={activeTab} onChange={handleTabChange}>
        <Tabs.List>
          <Tabs.Tab fw={500} value="ai">
            {t("AI")}
          </Tabs.Tab>
          <Tabs.Tab fw={500} value="mcp">
            {t("MCP")}
          </Tabs.Tab>
          <Tabs.Tab fw={500} value="ask">
            {t("Ask AI")}
          </Tabs.Tab>
          <Tabs.Tab fw={500} value="gaps">
            {t("Knowledge gaps")}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="ai" pt="md">
          {!hasAccess && (
            <Alert
              icon={<IconInfoCircle />}
              title={upgradeLabel}
              color="blue"
              mb="lg"
            >
              {t(
                "AI is only available in the Docmost enterprise edition. Contact sales@docmost.com.",
              )}
            </Alert>
          )}

          <Stack gap="md">
            {!isCloud() && <EnableAiSearch />}
            <EnableGenerativeAi />
            <EnableAiChat />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="mcp" pt="md">
          <McpSettings />
        </Tabs.Panel>

        <Tabs.Panel value="ask" pt="md">
          <AskAiSpaces />
        </Tabs.Panel>

        <Tabs.Panel value="gaps" pt="md">
          <AskAiKnowledgeGaps />
        </Tabs.Panel>
      </Tabs>
    </>
  );
}
